import React, { useEffect, useRef } from 'react';

import { Flex } from '@chakra-ui/react';
import { getApplicableL2CompanyModules } from 'Deer/components/navigation/secondary/Company/CompanyModules';
import { L3_PATHS } from 'Deer/constants/routes';
import { useCustomNameGoalsCompanyTabs } from 'Deer/hooks/useCustomNameGoalsCompanyTabs';
import { GoalsIntentLandingPage } from 'Deer/intent/Goals/GoalsIntentLandingPage';
import { IntentDropdown } from 'Deer/intent/Goals/components/IntentDropdown';
import { Layout } from 'Deer/intent/components/Layout/Layout';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setSelectedView } from 'reducers/viewsSlice';
import { AgGridTreeView } from 'screens/Team/Rabbit/Goals/AgGridTreeView';
import Header from 'screens/Team/Rabbit/Goals/Header';
import { useSelectIsArchivedViewVisible } from 'screens/Team/Rabbit/Goals/slice/GoalsSlice';
import { checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';

import { ArrowBack } from 'assets/assetsV1/iconComponents';

import { modifyString, StringModifier } from 'constants/copyHelpers';
import { sendPageViewEvent } from 'constants/mixpanelHelper';

import { BreadCrumb, BreadcrumbItem, BreadcrumbLink } from 'uiToolkitV1/BreadCrumb';
import { Button } from 'uiToolkitV1/Button';
import { PageTabbedNavigationV1 } from 'uiToolkitV1/PageTabbedNavigationV1';
import { TextHeader2, TextLabel } from 'uiToolkitV1/Typography';

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cohort: any;
  headerLabel: string;
}

const CompanyManageGoals = ({ cohort, headerLabel }: Props) => {
  const history = useHistory();
  const gridRef = useRef();

  const dispatch = useDispatch();
  const { tabs } = useCustomNameGoalsCompanyTabs();
  const { moduleName } = useOrganisationSettings();
  const isArchivedView = useSelectIsArchivedViewVisible();
  const modifiedHeaderLabel = `${headerLabel} ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`;
  const applicableL2CompanyModules = getApplicableL2CompanyModules(moduleName) ?? [];

  const goBack = () => {
    const urlToPush = L3_PATHS.company.goal.manage;
    history.push(urlToPush);
  };

  useEffect(() => {
    dispatch(setSelectedView({ ...cohort, teamId: cohort.id }));
  }, [cohort]);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      dispatch(setSelectedView({}));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        page: 'Company-goals-manage',
      },
    });
  }, []);

  if (checkIntentLandingPagesFeatureEnabled()) {
    return (
      <Layout
        Filters={
          <IntentDropdown
            gridRef={gridRef}
            breadcrumbs={{
              items: [
                {
                  label: 'Company',
                  to: applicableL2CompanyModules[0]?.value,
                },
              ],
              page: {
                label: modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE),
                to: '',
              },
            }}
          />
        }
      >
        <GoalsIntentLandingPage gridRef={gridRef} />
      </Layout>
    );
  }

  return (
    <>
      {!isArchivedView && <Header teamName={modifiedHeaderLabel} />}
      {isArchivedView && (
        <Flex justifyContent="flex-start" alignItems="center">
          <Button mr="8px" StartIcon={ArrowBack} variant="tertiary" onClick={goBack} />
          <Flex flexDir="column">
            <BreadCrumb>
              <BreadcrumbItem>
                <BreadcrumbLink to={L3_PATHS.company.goal.manage}>{`${modifyString(
                  moduleName,
                  StringModifier.PLURAL_TITLE_CASE
                )}`}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <TextLabel bold>Archived</TextLabel>
              </BreadcrumbItem>
            </BreadCrumb>
            <TextHeader2 bold>{`Archived ${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)}`}</TextHeader2>
          </Flex>
        </Flex>
      )}
      <PageTabbedNavigationV1 tabs={tabs} />

      <AgGridTreeView showHierarchyToggle={false} />
    </>
  );
};

export default CompanyManageGoals;
